/**
 * The Sketch's words and numbers in the reader's language (docs/contributing/i18n.md), for components and for the
 * pure helpers that make text (gaps.ts, assist.ts, variants.ts…): they take a `Text` instead of building English.
 * Components get it from `useSketchText()` (useText.ts); tests from `testText()` (testText.ts, the English catalog).
 * Numbers, lengths, Lux and money always go through Intl with the user's locale, never `toFixed`.
 */
import type { IntlShape } from "react-intl";
import type { MessageKey } from "../i18n/keys";

export type Values = Record<string, string | number | Date>;

export interface Text {
  /** The catalog message `key` with ICU `values` (a unit's value isolated in right-to-left locales). */
  t: (key: MessageKey, values?: Values) => string;
  /** A number with exactly `digits` decimals ("1.234,5" in German). */
  num: (value: number, digits?: number) => string;
  /** A number with at most `digits` decimals. */
  upTo: (value: number, digits?: number) => string;
  /** A Lux amount from hundredths, always two decimals ("42.50"). */
  lux: (hundredths: number) => string;
  /** A Lux price: whole amounts without decimals ("5"), else two ("0.10"). */
  luxPrice: (hundredths: number) => string;
  /** Money from minor units (cents). */
  money: (minor: number, currency?: string) => string;
  /** Money in major units, whole by default ("$12,340"). */
  amount: (major: number, currency?: string, digits?: number) => string;
  /** A percentage from 0–100 ("45 %" in French). */
  pct: (value: number, digits?: number) => string;
  /** A date, e.g. `{ month: "short", day: "numeric" }`. */
  date: (value: Date | string | number, options: Intl.DateTimeFormatOptions) => string;
  /** "a, b and c" in the reader's language. */
  list: (items: string[]) => string;
}

type Shape = Pick<IntlShape, "locale" | "formatMessage" | "formatNumber" | "formatDate" | "formatList">;

/** First-strong isolates (FSI … PDI): in a right-to-left page a number with a Latin unit ("5 m") stays one run in
 * its own order instead of reading "m 5". */
const ISOLATE = ["\u2068", "\u2069"];

export function textOf(intl: Shape): Text {
  const num = (value: number, digits = 0) => intl.formatNumber(value, { minimumFractionDigits: digits, maximumFractionDigits: digits });
  const rtl = /^(ar|he|fa|ur)(-|$)/.test(intl.locale);
  return {
    t: (key, values) => {
      const text = intl.formatMessage({ id: key }, values as never) as string;
      return rtl && key.startsWith("sketch.unit.") ? `${ISOLATE[0]}${text}${ISOLATE[1]}` : text;
    },
    num,
    upTo: (value, digits = 2) => intl.formatNumber(value, { maximumFractionDigits: digits }),
    lux: (hundredths) => num(Math.trunc(hundredths) / 100, 2),
    luxPrice: (hundredths) => num(Math.trunc(hundredths) / 100, hundredths % 100 === 0 ? 0 : 2),
    money: (minor, currency = "USD") => intl.formatNumber(Math.trunc(minor) / 100, { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    amount: (major, currency = "USD", digits = 0) => intl.formatNumber(major, { style: "currency", currency, minimumFractionDigits: digits, maximumFractionDigits: digits }),
    pct: (value, digits = 0) => intl.formatNumber(value / 100, { style: "percent", minimumFractionDigits: digits, maximumFractionDigits: digits }),
    date: (value, options) => intl.formatDate(value, options),
    list: (items) => intl.formatList(items, { type: "conjunction" }) as string,
  };
}

/** A length in metres as the drawings label it: centimetres under a metre, else metres (2 decimals under 10 m). */
export function metres(tx: Text, m: number): string {
  if (m < 0.995) return tx.t("sketch.unit.cm", { value: tx.num(Math.max(1, Math.round(m * 100)), 0) });
  return tx.t("sketch.unit.m", { value: tx.num(m, m < 10 ? 2 : 1) });
}

/** The width an SVG label needs at `per` px a Latin letter: CJK and other wide characters count double. */
export function labelWidth(text: string, per: number): number {
  let w = 0;
  for (const ch of text) w += /[\u1100-\u115f\u2e80-\ua4cf\uac00-\ud7a3\uf900-\ufaff\ufe30-\ufe4f\uff00-\uff60\uffe0-\uffe6]/u.test(ch) ? 2 * per : per;
  return w;
}

/** A roof face's name: "Face 3" (in the reader's language) unless the face carries a name of its own. */
export function faceName(tx: Text, label: string | null | undefined, index: number): string {
  const n = label ? /^Face (\d{1,4})$/.exec(label) : null;
  if (!label || n) return tx.t("sketch.face.n", { n: n ? Number(n[1]) : index + 1 });
  return label;
}

const COMPASS: MessageKey[] = ["sketch.compass.n", "sketch.compass.ne", "sketch.compass.e", "sketch.compass.se", "sketch.compass.s", "sketch.compass.sw", "sketch.compass.w", "sketch.compass.nw"];

/** The compass point an azimuth (degrees from north, clockwise) faces: "SE". */
export function compassName(tx: Text, az: number): string {
  return tx.t(COMPASS[Math.round((((az % 360) + 360) % 360) / 45) % 8]!);
}
